import React, { useState, useCallback } from 'react';

const MULTI_RE = /^multi:(\d+):(\d+):(\d+)$/;

function getExt(name) {
    if (!name) return '';
    const i = name.lastIndexOf('.');
    return i >= 0 ? name.slice(i + 1).toLowerCase().split('?')[0] : '';
}

function getKindIcon(ext) {
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'heic', 'avif'].includes(ext)) return { icon: 'fas fa-image', color: '#0094ff' };
    if (['mp4', 'webm', 'mov', 'avi', 'mkv'].includes(ext)) return { icon: 'fas fa-video', color: '#9b59b6' };
    if (['mp3', 'wav', 'ogg', 'm4a'].includes(ext)) return { icon: 'fas fa-music', color: '#6f42c1' };
    if (ext === 'pdf') return { icon: 'fas fa-file-pdf', color: '#e74c3c' };
    if (['doc', 'docx'].includes(ext)) return { icon: 'fas fa-file-word', color: '#2b579a' };
    if (['xls', 'xlsx', 'csv'].includes(ext)) return { icon: 'fas fa-file-excel', color: '#1b8053' };
    if (['ppt', 'pptx'].includes(ext)) return { icon: 'fas fa-file-powerpoint', color: '#d24726' };
    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return { icon: 'fas fa-file-archive', color: '#6b7785' };
    return { icon: 'fas fa-file', color: '#6b7785' };
}

function stripUuid(name) {
    if (!name) return '';
    const fullUuid = name.match(/^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}(\.[^.]+)?$/i);
    if (fullUuid) return `archivo${fullUuid[1] || ''}`;
    return name.replace(/^[a-f0-9]{8}_/i, '');
}

export function FilesModal({ surveyId, questionId, files: filesProp = null, onClose }) {
    const [files, setFiles] = useState(filesProp || null);
    const [loading, setLoading] = useState(!filesProp);
    const [error, setError] = useState(null);

    React.useEffect(() => {
        // Si nos pasan los archivos directo (ej: lista de subform), no hacemos fetch.
        if (filesProp) {
            setFiles(filesProp);
            setLoading(false);
            return;
        }
        let cancelled = false;
        fetch(`/formatos/records/files/${surveyId}/${questionId}`, {
            headers: { 'Content-Type': 'application/json' }
        })
            .then(r => r.json())
            .then(json => {
                if (cancelled) return;
                setFiles(json.files || []);
                setLoading(false);
            })
            .catch(err => {
                if (cancelled) return;
                setError('No se pudieron cargar los archivos');
                setLoading(false);
            });
        return () => { cancelled = true; };
    }, [surveyId, questionId, filesProp]);

    return (
        <div className="cm-files-modal-backdrop" onClick={onClose} role="dialog">
            <div className="cm-files-modal" onClick={(e) => e.stopPropagation()}>
                <div className="cm-files-modal__header">
                    <div className="cm-files-modal__title">
                        <i className="fas fa-folder-open mr-2" />
                        Archivos
                        {files && <span className="cm-files-modal__count">{files.length}</span>}
                    </div>
                    <button type="button" className="cm-files-modal__close" onClick={onClose} aria-label="Cerrar">
                        <i className="fas fa-times" />
                    </button>
                </div>

                <div className="cm-files-modal__body">
                    {loading && (
                        <div className="cm-files-modal__loading">
                            <i className="fas fa-spinner fa-spin mr-2" /> Cargando...
                        </div>
                    )}

                    {error && (
                        <div className="cm-files-modal__error">
                            <i className="fas fa-exclamation-triangle mr-2" /> {error}
                        </div>
                    )}

                    {!loading && !error && files && files.length === 0 && (
                        <div className="cm-files-modal__empty">
                            <i className="fas fa-folder-open mr-2" /> Sin archivos
                        </div>
                    )}

                    {!loading && !error && files && files.length > 0 && (
                        <ul className="cm-files-modal__list">
                            {files.map((f) => {
                                const displayName = stripUuid(f.name);
                                const ext = getExt(displayName);
                                const kind = getKindIcon(ext);
                                return (
                                    <li key={f.id} className="cm-files-modal__item">
                                        <div className="cm-files-modal__item-icon" style={{ color: kind.color }}>
                                            <i className={kind.icon} />
                                        </div>
                                        <div className="cm-files-modal__item-info">
                                            <a
                                                href={f.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="cm-files-modal__item-name"
                                                title={displayName}
                                            >
                                                {displayName}
                                            </a>
                                        </div>
                                        <a
                                            href={f.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="cm-files-modal__item-action"
                                            title="Abrir en nueva pestaña"
                                        >
                                            <i className="fas fa-external-link-alt" />
                                        </a>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
}

export default function FileCell({ value, column }) {
    const [modalOpen, setModalOpen] = useState(false);

    if (!value) return <span />;

    // Detectar marker multi: "multi:N:survey_id:question_id"
    const multiMatch = typeof value === 'string' && value.match(MULTI_RE);

    if (multiMatch) {
        const count = parseInt(multiMatch[1], 10);
        const surveyId = multiMatch[2];
        const questionId = multiMatch[3];
        return (
            <>
                <button
                    type="button"
                    className="cm-cell-file-link cm-cell-file-link--multi"
                    onClick={(e) => { e.stopPropagation(); setModalOpen(true); }}
                >
                    <i className="fas fa-copy" style={{ marginRight: 4 }} />
                    Ver archivos
                    <span className="cm-cell-file-link__badge">{count}</span>
                </button>
                {modalOpen && (
                    <FilesModal
                        surveyId={surveyId}
                        questionId={questionId}
                        onClose={() => setModalOpen(false)}
                    />
                )}
            </>
        );
    }

    return (
        <a href={value} target="_blank" rel="noopener noreferrer" className="cm-cell-file-link">
            <i className="fas fa-file" style={{ marginRight: 4 }} />
            Ver archivo
        </a>
    );
}
